import { useEffect, useMemo, useRef } from "react";

import type { TagsToolsetRailController } from "@/viewport-tools/rail/types";
import { VIEWPORT_TOOLSET_IDS } from "@/viewport-tools/core/contracts";
import type { TagToolId } from "@/viewport-tools/tags/registry";

export interface UseTagsToolsetRailControllerArgs {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  activeTool: TagToolId | null;
  disabled?: boolean;
  tagOptions: Array<{ id: string; label: string }>;
  selectedTagOptionId: string | null;
  onSelectedTagOptionChange: (tagId: string) => void;
  onToolSelect: (toolId: TagToolId | null) => void;
}

export function useTagsToolsetRailController({
  expanded,
  onExpandedChange,
  activeTool,
  disabled,
  tagOptions,
  selectedTagOptionId,
  onSelectedTagOptionChange,
  onToolSelect,
}: UseTagsToolsetRailControllerArgs): TagsToolsetRailController {
  const wasExpandedRef = useRef(expanded);

  useEffect(() => {
    const didCollapse = wasExpandedRef.current && !expanded;
    wasExpandedRef.current = expanded;

    if (!didCollapse || !activeTool) {
      return;
    }

    onToolSelect(null);
  }, [activeTool, expanded, onToolSelect]);

  return useMemo(
    () => ({
      toolsetId: VIEWPORT_TOOLSET_IDS.tags,
      expanded,
      setExpanded: onExpandedChange,
      activeTool,
      disabled,
      tagOptions,
      selectedTagOptionId,
      setSelectedTagOptionId: onSelectedTagOptionChange,
      selectTool: onToolSelect,
    }),
    [
      activeTool,
      disabled,
      expanded,
      onExpandedChange,
      onSelectedTagOptionChange,
      onToolSelect,
      selectedTagOptionId,
      tagOptions,
    ]
  );
}
